// WatchToggle — "Beobachten"-Umschalter für eine Aufgabe (Task-Watcher).
//
// Beobachter erhalten die Status- und Kommentar-Benachrichtigungen der Aufgabe.
// Lädt den aktuellen Zustand (beobachte ich? wie viele beobachten?) beim Mounten
// und schaltet per POST/DELETE /api/tasks/:id/watch um. Nur für Bearbeitende
// sinnvoll — die Route verlangt editor/owner (der Aufrufer blendet ihn bei
// Nur-Lese-Zugriff aus).

import { useEffect, useState } from "preact/hooks";
import { Eye, EyeOff } from "lucide-preact";
import { api } from "../lib/api";
import { toastError } from "../lib/toast";

interface WatchState {
  watching: boolean;
  count: number;
}

export function WatchToggle({ taskId }: { taskId: number }): preact.JSX.Element | null {
  const [state, setState] = useState<WatchState | null>(null);
  // Vom "noch nicht geladen" (state === null, keine Anzeige) UNTERSCHEIDBAR
  // halten: sonst sieht ein fehlgeschlagener Abruf identisch aus wie "diese
  // Aufgabe hat keinen Beobachten-Schalter" — genau der Fehler, der in
  // Favorites.tsx zwei Monate unentdeckt blieb, weil er wie "da ist nichts"
  // aussah.
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Beim Öffnen den aktuellen Beobachtungs-Status laden. `silentError`
  // bewusst: das feuert bei jedem Öffnen der Aufgabe, ein Toast je Fehlschlag
  // wäre zu aufdringlich für ein Nebenfeature — aber die Kachel unten macht
  // den Fehlschlag trotzdem SICHTBAR statt einfach zu verschwinden.
  useEffect(() => {
    let alive = true;
    api<WatchState>(`/api/tasks/${taskId}/watch`, { silentError: true })
      .then((s) => {
        if (alive) setState(s);
      })
      .catch(() => {
        if (alive) setLoadFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [taskId]);

  if (loadFailed) {
    return (
      <span
        class="shrink-0 mt-0.5 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] border border-[color:var(--color-outline-variant)] opacity-70"
        title="Beobachten-Status konnte nicht geladen werden."
      >
        <EyeOff size={14} aria-hidden="true" />
        Beobachten nicht verfügbar
      </span>
    );
  }

  if (state === null) return null;

  const toggle = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    const next = !state.watching;
    try {
      const res = await api<WatchState>(`/api/tasks/${taskId}/watch`, {
        method: next ? "POST" : "DELETE",
      });
      setState(res);
    } catch {
      toastError(
        next
          ? "Beobachten fehlgeschlagen."
          : "Beobachtung konnte nicht beendet werden."
      );
    } finally {
      setBusy(false);
    }
  };

  const { watching, count } = state;

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy}
      class={`shrink-0 mt-0.5 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-60 ${
        watching
          ? "bg-[color:var(--color-primary-container,#DBEAFE)] text-[color:var(--color-primary)] border border-[color:var(--color-primary)]/40"
          : "border border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
      }`}
      aria-pressed={watching}
      title={
        watching
          ? "Du wirst über Status- und Kommentar-Änderungen benachrichtigt"
          : "Über Status- und Kommentar-Änderungen benachrichtigt werden"
      }
      data-testid="watch-toggle"
    >
      {watching ? (
        <Eye size={14} aria-hidden="true" />
      ) : (
        <EyeOff size={14} aria-hidden="true" />
      )}
      {watching ? "Beobachtet" : "Beobachten"}
      {count > 0 && (
        <span class="tabular-nums opacity-70">{count}</span>
      )}
    </button>
  );
}
